import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Star, Plus, ArrowDownLeft, Bell, ExternalLink, ArrowLeft, Newspaper, PiggyBank, PencilRuler, LineChart as LineIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Panel, Skeleton, Segmented, GainBadge } from "../components/ui";
import { BuyDialog, SellDialog, AlertDialog } from "../components/Dialogs";
import PriceChart from "../components/PriceChart";
import AdvancedChart from "../components/AdvancedChart";
import BenchmarkCompare from "../components/BenchmarkCompare";
import Hint from "../components/Hint";
import { Fundamentals, AnalystPanel, EarningsPanel } from "../components/InstrumentInsights";
import InstrumentLogo from "../components/InstrumentLogo";
import { api } from "../lib/api";
import { usd, pct, gainColor, shortDate, money } from "../lib/format";
import { useTranslation } from "react-i18next";
import useRefreshOnReturn from "../lib/useRefreshOnReturn";

// Chart range buttons. The candle interval for each is decided server-side
// (market_data.CHART_RANGES), so 1D/1W/1M come back as intraday candles.
const PERIODS = ["1d", "1w", "1m", "1y", "5y", "all"];
const CHART_TYPES = ["line", "area", "candlestick", "heikin", "ohlc", "baseline"];
const SMA_OPTIONS = [20, 50, 100, 200, 500];

export default function Instrument() {
  const { t } = useTranslation();
  const { ticker } = useParams();
  const [quote, setQuote] = useState(null);
  const [chart, setChart] = useState([]);
  const [chartIntraday, setChartIntraday] = useState(false);
  const [chartType, setChartType] = useState("area");
  const [chartMode, setChartMode] = useState("standard");
  const [smas, setSmas] = useState([50, 200]);
  const [period, setPeriod] = useState("1y");
  const [loadingChart, setLoadingChart] = useState(true);
  const [news, setNews] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [watched, setWatched] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  // Whether the signed-in user actually holds this instrument. Selling is offered
  // here for the same reason buying is — the decision happens on this page — but a
  // Sell button on something you do not own can only ever fail.
  const [heldQty, setHeldQty] = useState(0);
  const [meta, setMeta] = useState({ logo: null, quote_type: null });

  const loadHeld = useCallback(() => {
    api.holdings()
      .then((r) => setHeldQty(r.data.find((h) => h.ticker === ticker.toUpperCase())?.quantity || 0))
      .catch(() => setHeldQty(0));
  }, [ticker]);

  // The header quote is the page's single source of truth for "what is this worth
  // right now" — see AnalystPanel, which is handed this same value rather than
  // reading a price of its own.
  const loadQuote = useCallback(() => {
    api.quote(ticker).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [ticker]);

  useEffect(() => {
    loadQuote();
    api.news(ticker).then((r) => setNews(r.data)).catch(() => setNews([]));
    api.instrumentDividends(ticker).then((r) => setDividends(r.data)).catch(() => setDividends([]));
    api.watchlist().then((r) => setWatched(r.data.some((w) => w.ticker === ticker.toUpperCase()))).catch(() => {});
    api.fundamentals(ticker).then((r) => setMeta({ logo: r.data.logo, quote_type: r.data.quote_type })).catch(() => setMeta({ logo: null, quote_type: null }));
    loadHeld();
  }, [ticker, loadQuote, loadHeld]);

  const loadChart = useCallback(() => {
    setLoadingChart(true);
    api.chartHistory(ticker, period)
      .then((r) => { setChart(r.data.data); setChartIntraday(!!r.data.intraday); })
      .catch(() => setChart([])).finally(() => setLoadingChart(false));
  }, [ticker, period]);
  useEffect(() => { loadChart(); }, [loadChart]);

  // Returning to a price page and seeing a stale price is the worst version of
  // this bug, so the quote and the chart both refetch.
  useRefreshOnReturn(() => { loadQuote(); loadChart(); loadHeld(); });

  const toggleWatch = async () => {
    try {
      if (watched) { await api.removeWatch(ticker); setWatched(false); toast.success(t("instrument.removedWatch")); }
      else { await api.addWatch({ ticker, name: quote?.name }); setWatched(true); toast.success(t("instrument.addedWatch")); }
    } catch { toast.error(t("dialogs.failed")); }
  };

  const recentDivs = [...dividends].reverse().slice(0, 8);
  const assetType = (meta.quote_type || quote?.asset_type || "EQUITY").toUpperCase();
  const isCrypto = assetType.includes("CRYPTO");
  const isFund = assetType === "ETF" || assetType === "MUTUALFUND";
  const isEquity = !isCrypto && !isFund;

  const newsPanel = (
    <Panel delay={0.1} className="card p-6" testid="instrument-news">
      <div className="flex items-center gap-2 mb-4"><Newspaper size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("instrument.latestNews")}</h3></div>
      <div className="space-y-3">
        {news.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">{t("instrument.noNews")}</div>}
        {news.map((n, i) => (
          <a key={i} href={n.url} target="_blank" rel="noreferrer" className="block p-3 rounded-xl hover:bg-slate-50 transition-colors group">
            <div className="text-sm font-medium text-slate-800 group-hover:text-brand-600 flex items-start gap-1.5">
              {n.title} <ExternalLink size={13} className="mt-1 shrink-0 opacity-0 group-hover:opacity-100" />
            </div>
            <div className="text-xs text-slate-400 mt-1">{n.publisher}</div>
          </a>
        ))}
      </div>
    </Panel>
  );

  const dividendsPanel = (
    <Panel delay={0.15} className="card p-6" testid="instrument-dividends">
      <div className="flex items-center gap-2 mb-4"><PiggyBank size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("instrument.dividendHistory")}</h3></div>
      <div className="space-y-2">
        {recentDivs.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">{t("instrument.noDividends")}</div>}
        {recentDivs.map((d, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0 text-sm">
            <span className="text-slate-500">{shortDate(d.date)}</span>
            <span className="tabular font-medium">{money(d.amount, quote?.currency || "USD")} {t("instrument.perShare")}</span>
          </div>
        ))}
      </div>
    </Panel>
  );

  return (
    <div className="space-y-8">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-600"><ArrowLeft size={16} /> {t("instrument.back")}</Link>

      {/* Header */}
      <Panel className="card p-6" testid="instrument-header">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <InstrumentLogo logo={meta.logo} ticker={ticker} size={56} rounded="rounded-2xl" />
            <div>
              <h1 className="text-2xl font-heading font-semibold tracking-tight">{ticker.toUpperCase()}</h1>
              <div className="text-slate-400 text-sm">{quote?.name || "—"} · {assetType}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {quote ? (
              <div className="text-right">
                <div className="text-3xl font-heading font-semibold tracking-tight tabular">{money(quote.price, quote.currency, { crypto: isCrypto })}</div>
                <div className={`text-sm tabular ${gainColor(quote.change_pct)}`}>{pct(quote.change_pct)} · {quote.currency}</div>
              </div>
            ) : <Skeleton className="h-12 w-32" />}
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <button data-testid="inst-watch-btn" onClick={toggleWatch} className={watched ? "btn-primary" : "btn-outline"}>
            <Star size={16} className={watched ? "fill-white" : ""} /> {watched ? t("instrument.watching") : t("instrument.watch")}
          </button>
          <button data-testid="inst-buy-btn" className="btn-outline" onClick={() => setBuyOpen(true)}><Plus size={16} /> {t("instrument.buy")}</button>
          {heldQty > 0 && (
            <button data-testid="inst-sell-btn" className="btn-outline" onClick={() => setSellOpen(true)}><ArrowDownLeft size={16} /> {t("instrument.sell")}</button>
          )}
          <button data-testid="inst-alert-btn" className="btn-outline" onClick={() => setAlertOpen(true)}><Bell size={16} /> {t("instrument.setAlert")}</button>
        </div>
      </Panel>

      {/* Chart */}
      <Panel delay={0.05} className="card p-4 sm:p-6" testid="instrument-chart">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-4">
          <Segmented options={[{ value: "standard", label: t("instrument.standard") }, { value: "advanced", label: t("instrument.advanced") }]} value={chartMode} onChange={setChartMode} testid="chart-mode-toggle" fullWidthOnMobile />
          <Segmented options={PERIODS.map((p) => ({ value: p, label: t(`time.${p.toUpperCase()}`) }))} value={period} onChange={setPeriod} testid="chart-period-toggle" fullWidthOnMobile />
        </div>

        {chartMode === "standard" && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
            <select data-testid="chart-type-select" value={chartType} onChange={(e) => setChartType(e.target.value)} className="input py-2 w-full sm:w-auto text-sm font-medium">
              {CHART_TYPES.map((ct) => <option key={ct} value={ct}>{t(`instrument.chartTypes.${ct}`)}</option>)}
            </select>
            <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0">
              <Hint term="SMA"><span className="text-xs text-slate-500 font-medium whitespace-nowrap shrink-0">{t("instrument.movingAverages")}</span></Hint>
              {SMA_OPTIONS.map((p) => {
                const on = smas.includes(p);
                return (
                  <button key={p} data-testid={`sma-toggle-${p}`}
                    onClick={() => setSmas((s) => on ? s.filter((x) => x !== p) : [...s, p])}
                    className={`pill px-2.5 py-1 border shrink-0 transition-colors ${on ? "bg-brand-50 text-brand-600 border-brand-200" : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loadingChart ? <Skeleton className="h-[280px] sm:h-[400px]" /> : (
          chartMode === "advanced" ? <AdvancedChart data={chart} isCrypto={isCrypto} intraday={chartIntraday} /> : <PriceChart data={chart} type={chartType} smas={smas} isCrypto={isCrypto} intraday={chartIntraday} />
        )}
      </Panel>

      <BenchmarkCompare ticker={ticker.toUpperCase()} range={period} />

      {/* Insights (adaptive by asset type) */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Fundamentals ticker={ticker.toUpperCase()} assetType={assetType} />
        {isEquity ? <AnalystPanel ticker={ticker.toUpperCase()} price={quote?.price ?? null} /> : newsPanel}
      </div>

      {isEquity && <EarningsPanel ticker={ticker.toUpperCase()} />}

      <div className="grid lg:grid-cols-2 gap-6">
        {isEquity && newsPanel}
        {!isCrypto && dividendsPanel}
      </div>

      <BuyDialog open={buyOpen} ticker={ticker.toUpperCase()} name={quote?.name} onClose={() => setBuyOpen(false)} />
      <SellDialog open={sellOpen} ticker={ticker.toUpperCase()} onClose={() => setSellOpen(false)} onDone={loadHeld} />
      <AlertDialog open={alertOpen} ticker={ticker.toUpperCase()} name={quote?.name} onClose={() => setAlertOpen(false)} />
    </div>
  );
}
